const MAILBOX_LINK_OUTLOOK = 'Please Link your Mailbox';
const MAILBOX_LINK_GMAIL = 'Please Link your Gmail Mailbox';

export const MAILBOX_LINK_MESSAGES = [
  MAILBOX_LINK_OUTLOOK,
  MAILBOX_LINK_GMAIL,
] as const;

/** Unwrap message from API / Lambda proxy / encrypted-decrypt shapes. */
export function normalizeApiMessage(res: unknown, depth = 0): string {
  if (res == null || depth > 4) {
    return '';
  }

  if (typeof res === 'string') {
    const trimmed = res.trim();
    if (!trimmed) {
      return '';
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return normalizeApiMessage(JSON.parse(trimmed), depth + 1);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (typeof res !== 'object') {
    return '';
  }

  const record = res as Record<string, unknown>;

  const directMessage = record['message'];
  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage.trim();
  }

  // Nested error shapes: { error: { message } } or HttpErrorResponse.error
  const nestedError = record['error'];
  if (nestedError != null) {
    const nestedMessage = normalizeApiMessage(nestedError, depth + 1);
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  // Lambda proxy: { statusCode: 200, body: '{"message":"..."}' }
  if (record['body'] != null) {
    const bodyMessage = normalizeApiMessage(record['body'], depth + 1);
    if (bodyMessage) {
      return bodyMessage;
    }
  }

  // Some handlers wrap payload: { data: { message } }
  if (record['data'] != null) {
    const dataMessage = normalizeApiMessage(record['data'], depth + 1);
    if (dataMessage) {
      return dataMessage;
    }
  }

  return '';
}

export function isMailboxLinkRequiredMessage(message: string): boolean {
  const normalized = message.trim();
  return (
    normalized === MAILBOX_LINK_OUTLOOK ||
    normalized === MAILBOX_LINK_GMAIL ||
    normalized.includes(MAILBOX_LINK_OUTLOOK) ||
    normalized.includes(MAILBOX_LINK_GMAIL)
  );
}

/** BE sometimes returns HTTP 200 with { message: 'Please Link your Mailbox' } or Lambda proxy body. */
export function isMailboxLinkRequiredResponse(res: unknown): boolean {
  return isMailboxLinkRequiredMessage(normalizeApiMessage(res));
}

export function getApiErrorMessage(error: unknown): string {
  return normalizeApiMessage(error);
}

export function toMailboxLinkError(res: unknown): {
  status: number;
  error: { message: string };
  message: string;
} {
  const message = normalizeApiMessage(res) || MAILBOX_LINK_OUTLOOK;
  return {
    status: 403,
    error: { message },
    message,
  };
}

/** Persistent red toast for mailbox-not-linked (BE returns HTTP 200). */
export function getMailboxLinkToastConfig(): {
  disableTimeOut: boolean;
  closeButton: boolean;
  tapToDismiss: boolean;
} {
  return {
    disableTimeOut: true,
    closeButton: true,
    tapToDismiss: false,
  };
}

export function showMailboxLinkRequiredToast(
  toast: { error: (message: string, title?: string, override?: object) => void },
  message?: string
): void {
  toast.error(
    message?.trim() || MAILBOX_LINK_OUTLOOK,
    'Email',
    getMailboxLinkToastConfig()
  );
}
